import { forwardRef, type CSSProperties } from "react";
import FbkCardShell from "./FbkCardShell";
import { ThemeOverlay } from "./ThemeOverlay";
import { resolveCardTheme } from "./theme";
import {
  defaultFooterLeft,
  resolveTextStyle,
  type CaptainPick,
  type CaptainPicksCardContent,
  type CardFormat,
} from "./types";

interface CaptainPicksCardProps {
  format: CardFormat;
  content: CaptainPicksCardContent;
}

function formatPickValue(
  value: number,
  mode: "percent" | "count",
): string {
  if (mode === "percent") return `${Math.round(value)}%`;
  // Compact thousands separator for big squad counts.
  return value.toLocaleString();
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
}

const CaptainPicksCard = forwardRef<HTMLDivElement, CaptainPicksCardProps>(
  function CaptainPicksCard({ format, content }, ref) {
    const isStory = format === "story";
    const style = content.style ?? {};
    const theme = resolveCardTheme(content.theme ?? "signature", {
      monoColor: content.monoColor,
    });
    const displayMode = content.displayMode ?? "percent";
    const picks = (content.picks ?? []).slice(0, 15);
    const showArmband = content.showCaptainArmband ?? true;

    // ─── Resolved text styles ────────────────────────────────────────
    const eyebrowStyle: CSSProperties = {
      ...resolveTextStyle(
        style.eyebrow,
        {
          fontFamily: theme.fontEyebrow,
          fontSizePx: 24,
          color: theme.accentColor,
          letterSpacing: "0.4em",
          bold: true,
        },
        format,
      ),
      textTransform: "uppercase",
      marginTop: isStory ? 40 : 24,
    };

    const titleDefaultSize = isStory ? 120 : 88;
    const titleStyle: CSSProperties = {
      ...resolveTextStyle(
        style.headline,
        {
          fontFamily: theme.fontHeadline,
          fontSizePx: titleDefaultSize,
          color: theme.headlineColor,
          letterSpacing: "-0.025em",
          bold: true,
        },
        format,
      ),
      lineHeight: 0.95,
      marginTop: isStory ? 16 : 10,
    };

    const goldStyle: CSSProperties = {
      ...resolveTextStyle(
        style.goldHighlight,
        {
          fontFamily: theme.fontHeadline,
          fontSizePx: titleDefaultSize,
          color: theme.accentColor,
          bold: true,
        },
        format,
      ),
    };

    const taglineStyle: CSSProperties | null = content.tagline
      ? {
          ...resolveTextStyle(
            style.tagline,
            {
              fontFamily: theme.fontBody,
              fontSizePx: isStory ? 26 : 20,
              color: theme.subtleTextColor,
              italic: true,
              bold: false,
            },
            format,
          ),
          marginTop: isStory ? 16 : 10,
          lineHeight: 1.3,
          maxWidth: isStory ? 880 : 720,
        }
      : null;

    // ─── Row sizing ──────────────────────────────────────────────────
    // Auto-scale the row height to fit the variable list count (5–15).
    // Locked-in baseline: a 10-row Story card needs ~85–95px per row to
    // breathe; we adjust ±15% by count to avoid clipping or float.
    const rowCount = Math.max(picks.length, 1);
    const baselineRowHeight = isStory ? 95 : 70;
    const rowGap = isStory ? 10 : 7;
    const targetTotalHeight = isStory ? rowCount * baselineRowHeight : rowCount * baselineRowHeight;
    void targetTotalHeight;

    const photoSize = isStory ? 78 : 60;
    const rankSize = isStory ? 48 : 38;
    const rankFontSize = isStory ? 22 : 18;
    const nameFontSize = isStory ? 28 : 22;
    const clubFontSize = isStory ? 16 : 13;
    const valueFontSize = isStory ? 36 : 28;
    const captainBadgeSize = isStory ? 36 : 28;

    return (
      <FbkCardShell
        ref={ref}
        format={format}
        footerLeft={content.footerLeft || defaultFooterLeft("CAPTAIN_PICKS")}
        footerRight={content.hashtags || "#WC2026 · #FBK"}
        header={content.header}
        background={style.background}
        customBaseGradient={theme.customBaseGradient}
        headerOnLight={theme.headerOnLight}
        suppressBrandOverlays={theme.suppressBrandOverlays}
        hideFooter={content.footer?.hide}
      >
        <ThemeOverlay theme={theme} format={format} />

        <div
          style={{
            position: "relative",
            zIndex: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {/* TITLE BLOCK */}
          <div style={eyebrowStyle}>{content.eyebrow || "MOST CAPTAINED"}</div>
          <div style={titleStyle}>
            {content.headline}
            {content.goldHighlight && (
              <>
                {" "}
                <span style={goldStyle}>{content.goldHighlight}</span>
              </>
            )}
          </div>
          {taglineStyle && <div style={taglineStyle}>{content.tagline}</div>}

          {/* RANKED LIST */}
          <div
            style={{
              marginTop: isStory ? 36 : 22,
              display: "flex",
              flexDirection: "column",
              gap: rowGap,
              flex: 1,
            }}
          >
            {picks.map((pick, i) => (
              <PickRow
                key={i}
                rank={i + 1}
                pick={pick}
                displayMode={displayMode}
                isTop3={i < 3}
                isStory={isStory}
                accentColor={theme.accentColor}
                headlineColor={theme.headlineColor}
                subtleColor={theme.subtleTextColor}
                photoSize={photoSize}
                rankSize={rankSize}
                rankFontSize={rankFontSize}
                nameFontSize={nameFontSize}
                clubFontSize={clubFontSize}
                valueFontSize={valueFontSize}
                captainBadgeSize={captainBadgeSize}
                showArmband={showArmband}
              />
            ))}
          </div>

          {/* TOTAL FOOTNOTE */}
          {content.totalLabel && (
            <div
              style={{
                marginTop: isStory ? 24 : 16,
                padding: `${isStory ? 14 : 10}px ${isStory ? 24 : 18}px`,
                borderRadius: 9999,
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.15)",
                fontFamily: theme.fontEyebrow,
                fontSize: isStory ? 18 : 14,
                fontWeight: 800,
                color: theme.subtleTextColor,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                alignSelf: "center",
                textAlign: "center",
              }}
            >
              {content.totalLabel}
            </div>
          )}
        </div>
      </FbkCardShell>
    );
  },
);

export default CaptainPicksCard;

// ─── Subcomponents ───────────────────────────────────────────────────────────

interface PickRowProps {
  rank: number;
  pick: CaptainPick;
  displayMode: "percent" | "count";
  isTop3: boolean;
  isStory: boolean;
  accentColor: string;
  headlineColor: string;
  subtleColor: string;
  photoSize: number;
  rankSize: number;
  rankFontSize: number;
  nameFontSize: number;
  clubFontSize: number;
  valueFontSize: number;
  captainBadgeSize: number;
  /** When false, the gold "C" sticker is suppressed (top-scorers cards). */
  showArmband: boolean;
}

/** Position pill palette — matches the badge colours from /fantasy-leaderboard. */
const POSITION_PILL: Record<NonNullable<CaptainPick["position"]>, { bg: string; fg: string }> = {
  GK: { bg: "#FEF3C7", fg: "#92400E" },
  DEF: { bg: "#DBEAFE", fg: "#1E40AF" },
  MID: { bg: "#D1FAE5", fg: "#065F46" },
  FWD: { bg: "#FEE2E2", fg: "#991B1B" },
};

const RANK_MEDAL: Record<number, { bg: string; fg: string }> = {
  1: { bg: "#FFD700", fg: "#0A4724" },
  2: { bg: "#D6D6D6", fg: "#0A2A1A" },
  3: { bg: "#CD7F32", fg: "#FFFFFF" },
};

function PickRow({
  rank,
  pick,
  displayMode,
  isTop3,
  isStory,
  accentColor,
  headlineColor,
  subtleColor,
  photoSize,
  rankSize,
  rankFontSize,
  nameFontSize,
  clubFontSize,
  valueFontSize,
  captainBadgeSize,
  showArmband,
}: PickRowProps) {
  const photoSrc = pick.photoUrl || undefined;
  const useRemotePhoto =
    !!photoSrc && !photoSrc.startsWith("data:") && !photoSrc.startsWith("/");
  const useRemoteCrest =
    !!pick.clubLogoUrl && !pick.clubLogoUrl.startsWith("data:");
  const flagSrc =
    pick.nationFlagUrl ||
    (pick.nationCode
      ? `https://flagcdn.com/w160/${pick.nationCode.toLowerCase()}.png`
      : undefined);
  const useRemoteFlag = !!flagSrc && !flagSrc.startsWith("data:");
  const medal = RANK_MEDAL[rank];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: isStory ? 18 : 14,
        padding: `${isStory ? 12 : 9}px ${isStory ? 20 : 16}px`,
        background: isTop3
          ? "rgba(255, 215, 0, 0.08)"
          : "rgba(255,255,255,0.06)",
        border: `1px solid ${isTop3 ? "rgba(255, 215, 0, 0.25)" : "rgba(255,255,255,0.12)"}`,
        borderRadius: isStory ? 18 : 14,
      }}
    >
      {/* Rank badge — gold/silver/bronze for top 3, faint for the rest */}
      <div
        style={{
          width: rankSize,
          height: rankSize,
          borderRadius: 9999,
          background: medal ? medal.bg : "rgba(255,255,255,0.16)",
          color: medal ? medal.fg : "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Bebas Neue', 'Anton', sans-serif",
          fontSize: rankFontSize,
          fontWeight: 800,
          flexShrink: 0,
          boxShadow: medal ? "0 4px 12px rgba(0,0,0,0.3)" : "none",
        }}
      >
        {rank}
      </div>

      {/* Photo + captain "C" badge top-right */}
      <div
        style={{
          position: "relative",
          width: photoSize,
          height: photoSize,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: photoSize,
            height: photoSize,
            borderRadius: 9999,
            overflow: "hidden",
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.7) 100%)",
            border: `3px solid ${accentColor}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {photoSrc ? (
            <img
              src={photoSrc}
              alt={pick.name}
              crossOrigin={useRemotePhoto ? "anonymous" : undefined}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <div
              style={{
                fontFamily: "'Bebas Neue', 'Anton', sans-serif",
                fontSize: photoSize * 0.45,
                fontWeight: 800,
                color: "#FFFFFF",
              }}
            >
              {initialsOf(pick.name)}
            </div>
          )}
        </div>
        {/* Gold "C" armband — shown when this card represents captain picks. */}
        {showArmband && (
          <div
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              width: captainBadgeSize,
              height: captainBadgeSize,
              borderRadius: 9999,
              background: accentColor,
              color: "#0A4724",
              border: "2px solid #FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "'Bebas Neue', 'Anton', sans-serif",
              fontSize: captainBadgeSize * 0.7,
              fontWeight: 800,
              boxShadow: "0 3px 8px rgba(0,0,0,0.35)",
            }}
          >
            C
          </div>
        )}
      </div>

      {/* Name + club + flag column */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            fontSize: nameFontSize,
            fontWeight: 800,
            color: headlineColor,
            letterSpacing: "-0.01em",
            lineHeight: 1.1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {pick.name}
        </div>
        {(pick.clubName || pick.clubLogoUrl || flagSrc) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 4,
            }}
          >
            {pick.clubLogoUrl && (
              <img
                src={pick.clubLogoUrl}
                alt={pick.clubName || ""}
                crossOrigin={useRemoteCrest ? "anonymous" : undefined}
                style={{
                  width: clubFontSize * 1.6,
                  height: clubFontSize * 1.6,
                  objectFit: "contain",
                  background: "#FFFFFF",
                  borderRadius: 4,
                  padding: 2,
                  flexShrink: 0,
                }}
              />
            )}
            {flagSrc && (
              <img
                src={flagSrc}
                alt={pick.nationCode || ""}
                crossOrigin={useRemoteFlag ? "anonymous" : undefined}
                style={{
                  width: clubFontSize * 1.7,
                  height: clubFontSize * 1.2,
                  objectFit: "cover",
                  borderRadius: 3,
                  border: "1px solid rgba(255,255,255,0.4)",
                  flexShrink: 0,
                }}
              />
            )}
            {pick.clubName && (
              <div
                style={{
                  fontFamily:
                    "'Plus Jakarta Sans', system-ui, sans-serif",
                  fontSize: clubFontSize,
                  fontWeight: 700,
                  color: subtleColor,
                  letterSpacing: "0.04em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {pick.clubName}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Position pill — only shown when the row carries a position. */}
      {pick.position && (
        <div
          style={{
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            fontSize: clubFontSize,
            fontWeight: 800,
            padding: `${isStory ? 6 : 4}px ${isStory ? 14 : 10}px`,
            borderRadius: 9999,
            background: POSITION_PILL[pick.position].bg,
            color: POSITION_PILL[pick.position].fg,
            letterSpacing: "0.1em",
            flexShrink: 0,
          }}
        >
          {pick.position}
        </div>
      )}

      {/* Pick value (count or percent) */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          flexShrink: 0,
          minWidth: isStory ? 130 : 100,
        }}
      >
        <div
          style={{
            fontFamily: "'Bebas Neue', 'Anton', sans-serif",
            fontSize: valueFontSize,
            fontWeight: 800,
            color: isTop3 ? accentColor : headlineColor,
            lineHeight: 1,
            letterSpacing: "-0.01em",
          }}
        >
          {formatPickValue(pick.pickValue, displayMode)}
        </div>
        <div
          style={{
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            fontSize: clubFontSize,
            fontWeight: 700,
            color: subtleColor,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            marginTop: 2,
          }}
        >
          {displayMode === "percent" ? "of squads" : "picks"}
        </div>
      </div>
    </div>
  );
}
